import React, { useState, useMemo } from 'react';
import { QueueDistressRecord, AccessTier } from '../../types';
import { isBlurredRadarRow } from '../../config/accessTiers';
import { getIsoQueuePortalUrl } from '../../lib/sourceLinks';
import { buildSuiteCrossUrl } from '../../lib/ghostSession';
import {
  AlertTriangle,
  Clock,
  TrendingUp,
  DollarSign,
  Lock,
  ExternalLink,
  Search,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Sliders,
} from 'lucide-react';

interface DistressedQueueRadarProps {
  radarTargets: QueueDistressRecord[];
  tier: AccessTier;
  onUnderwriteTarget: (target: QueueDistressRecord) => void;
  onOpenAuthModal: () => void;
}

export const DistressedQueueRadar: React.FC<DistressedQueueRadarProps> = ({
  radarTargets,
  tier,
  onUnderwriteTarget,
  onOpenAuthModal,
}) => {
  const [selectedIso, setSelectedIso] = useState<string>('ALL');
  const [minScore, setMinScore] = useState<number>(20);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  const filteredTargets = useMemo(() => {
    return radarTargets.filter((t) => {
      if (selectedIso !== 'ALL' && t.iso_rto.toUpperCase() !== selectedIso) return false;
      if (t.distress_score < minScore) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return (
          t.queue_request_id.toLowerCase().includes(s) ||
          (t.canonical_name && t.canonical_name.toLowerCase().includes(s)) ||
          (t.county && t.county.toLowerCase().includes(s)) ||
          (t.state && t.state.toLowerCase().includes(s))
        );
      }
      return true;
    });
  }, [radarTargets, selectedIso, minScore, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredTargets.length / pageSize));
  const paginatedTargets = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTargets.slice(start, start + pageSize);
  }, [filteredTargets, page, pageSize]);

  const getClassificationBadge = (classification: string, score: number) => {
    switch (classification) {
      case 'CRITICAL':
        return (
          <span className="px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 font-mono font-bold text-[10px] flex items-center whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mr-1.5 animate-pulse" />
            CRITICAL ({score}/100)
          </span>
        );
      case 'ELEVATED':
        return (
          <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-400 font-mono font-bold text-[10px] flex items-center whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5" />
            ELEVATED ({score}/100)
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-400 font-mono font-semibold text-[10px] whitespace-nowrap">
            MONITORED ({score}/100)
          </span>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#06090e] overflow-hidden select-none">
      {/* 1. Header & Filter Bar */}
      <div className="p-3 bg-[#0c121e] border-b border-[#1a253b] flex flex-wrap items-center justify-between gap-3">
        {/* Search */}
        <div className="flex items-center space-x-2 flex-1 min-w-[280px] max-w-md">
          <div className="relative w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-slate-500" />
            <input
              type="text"
              placeholder="Search queue ID, campus name, county..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 outline-none hover:border-slate-700 focus:border-[#2979ff] transition"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-2 text-xs flex-wrap gap-y-2">
          {/* ISO Selector */}
          <select
            value={selectedIso}
            onChange={(e) => {
              setSelectedIso(e.target.value);
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value="ALL">All ISO / RTO Queues</option>
            <option value="PJM">PJM Interconnection</option>
            <option value="ERCOT">ERCOT (Texas)</option>
            <option value="NYISO">NYISO (New York)</option>
            <option value="MISO">MISO (Midcontinent)</option>
            <option value="CAISO">CAISO (California)</option>
          </select>

          {/* Distress Threshold */}
          <select
            value={minScore}
            onChange={(e) => {
              setMinScore(Number(e.target.value));
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded-lg px-2.5 py-1.5 outline-none hover:border-slate-700 focus:border-[#2979ff] font-mono text-xs"
          >
            <option value={0}>All Distress Levels (0+)</option>
            <option value={25}>Distress Score 25+ (Elevated)</option>
            <option value={50}>Distress Score 50+ (High)</option>
            <option value={75}>Distress Score 75+ (Critical Only)</option>
          </select>
        </div>

        {/* Counter */}
        <div className="text-slate-400 font-mono text-[11px]">
          Tracking <b className="text-white">{filteredTargets.length}</b> distressed queue positions
        </div>
      </div>

      {/* 2. Full-Width Tabular Telemetry Table */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-[#1a253b] bg-[#0c121e] text-slate-400 font-mono text-[11px] uppercase tracking-wider">
              <th className="py-3 px-4 font-semibold">Queue Request ID &amp; ISO</th>
              <th className="py-3 px-4 font-semibold">Target Campus / Interconnection</th>
              <th className="py-3 px-4 font-semibold text-right">Capacity (MW)</th>
              <th className="py-3 px-4 font-semibold text-right">COD Slippage</th>
              <th className="py-3 px-4 font-semibold text-right">Cost Escalation</th>
              <th className="py-3 px-4 font-semibold text-right">Sponsor CCCR</th>
              <th className="py-3 px-4 font-semibold text-right">Est. Buyout Range</th>
              <th className="py-3 px-4 font-semibold text-center">Source Portals</th>
              <th className="py-3 px-4 font-semibold text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-[#1a253b]/60">
            {paginatedTargets.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-16 text-center text-slate-500 font-mono">
                  No distressed queue positions match active filters.
                </td>
              </tr>
            ) : (
              paginatedTargets.map((target, idx) => {
                const isBlurred = isBlurredRadarRow(idx, tier);
                const isoLink = getIsoQueuePortalUrl(target.iso_rto, target.queue_request_id);

                return (
                  <tr
                    key={target.telemetry_id}
                    className={`transition-colors group hover:bg-[#0c121e]/80 ${
                      isBlurred ? 'filter blur-[2px] opacity-40 select-none' : ''
                    }`}
                  >
                    {/* 1. Queue Request & ISO */}
                    <td className="py-3 px-4 font-mono">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-bold text-white text-xs">
                            {target.queue_request_id}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded bg-[#162035] text-slate-300 font-mono border border-[#1a253b]">
                            {target.iso_rto}
                          </span>
                        </div>
                        <div>
                          {getClassificationBadge(target.distress_classification, target.distress_score)}
                        </div>
                      </div>
                    </td>

                    {/* 2. Target Campus */}
                    <td className="py-3 px-4">
                      <div className="space-y-0.5">
                        <div className="font-semibold text-white group-hover:text-[#2979ff] transition-colors leading-tight">
                          {target.canonical_name || target.canonical_project_id}
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono">
                          {target.county} County, {target.state} &bull; {target.canonical_project_id}
                        </div>
                      </div>
                    </td>

                    {/* 3. Capacity */}
                    <td className="py-3 px-4 text-right font-mono font-bold text-white">
                      {target.capacity_mw ? `${target.capacity_mw.toLocaleString()} MW` : 'N/A'}
                    </td>

                    {/* 4. COD Slippage */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className="text-rose-400 font-bold">
                        +{target.cod_slippage_months} Mos
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {target.current_study_cod ? `COD: ${target.current_study_cod}` : 'Delayed'}
                      </div>
                    </td>

                    {/* 5. Cost Escalation */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className="text-amber-400 font-bold">
                        +{target.upgrade_cost_escalation_pct?.toFixed(0)}%
                      </div>
                      <div className="text-[10px] text-slate-500">
                        ${target.latest_network_upgrade_usd ? (target.latest_network_upgrade_usd / 1_000_000).toFixed(1) : 0}M Upgrades
                      </div>
                    </td>

                    {/* 6. Sponsor CCCR */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className={target.sponsor_carrying_capacity_ratio && target.sponsor_carrying_capacity_ratio < 1.0 ? 'text-rose-400 font-bold' : 'text-slate-200'}>
                        {target.sponsor_carrying_capacity_ratio ? `${target.sponsor_carrying_capacity_ratio.toFixed(2)}x` : '0.45x'}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {target.sponsor_carrying_capacity_ratio && target.sponsor_carrying_capacity_ratio < 1.0 ? 'Deficit' : 'Adequate'}
                      </div>
                    </td>

                    {/* 7. Est Buyout Range */}
                    <td className="py-3 px-4 text-right font-mono">
                      <div className="text-emerald-400 font-bold text-xs">
                        ${target.estimated_buyout_min_usd ? (target.estimated_buyout_min_usd / 1_000_000).toFixed(1) : 0}M - ${target.estimated_buyout_max_usd ? (target.estimated_buyout_max_usd / 1_000_000).toFixed(1) : 0}M
                      </div>
                      <div className="text-[10px] text-slate-500">
                        Secondary Price
                      </div>
                    </td>

                    {/* 8. Source Portals */}
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-1.5">
                        {/* ISO Portal */}
                        <a
                          href={isoLink.url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-0.5 rounded bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] text-rose-400 text-[10px] font-mono flex items-center space-x-1 transition"
                          title={`Inspect public queue filing in ${target.iso_rto}`}
                        >
                          <span>{target.iso_rto}</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>

                        {/* Monitor Telemetry */}
                        <a
                          href={buildSuiteCrossUrl('https://monitor.aixenergy.io', { request: target.queue_request_id })}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-0.5 rounded bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] text-[#2979ff] text-[10px] font-mono flex items-center space-x-1 transition"
                          title="Open queue study telemetry in AIxEnergy Monitor"
                        >
                          <span>Monitor</span>
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      </div>
                    </td>

                    {/* 9. Action Button */}
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => onUnderwriteTarget(target)}
                        className="px-2.5 py-1 rounded bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-400 hover:text-white font-mono text-[11px] font-semibold transition"
                      >
                        Underwrite &rarr;
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 3. Pagination Footer */}
      <div className="p-3 bg-[#0c121e] border-t border-[#1a253b] flex items-center justify-between text-xs text-slate-400 font-mono">
        <div className="flex items-center space-x-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="bg-[#06090e] border border-[#1a253b] text-slate-200 rounded px-2 py-1 outline-none text-xs"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-slate-500">| Page {page} of {totalPages}</span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
            title="Previous Page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded bg-[#121a2d] border border-[#1a253b] text-slate-300 disabled:opacity-30 hover:bg-[#1a253b] transition"
            title="Next Page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
